/**
 * Builds ProjectAnalysisInput from useScenarios + per-scenario node data + Scene InstanceModels.
 * When DOWNLOADS_DRAINED_EVENT fires, this hook loads model node data from OPFS into the
 * SceneManager Scene (InstanceModels) so scene.getModel(modelId) is available for analysis.
 */
import type {
  ProjectAnalysisInput,
  ProjectAnalysisModelEntry,
  ProjectAnalysisScenarioEntry,
} from "@/types/analysis/ProjectAnalysisInput";
import { DownloadManagerEvents } from "@/types/DownloadManager/DownloadManager";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useScenarios } from "@api/scenarios";
import { Co2MaterialType } from "@api/apiClient";
import { useCo2MaterialTypesByIds } from "@api/co2MaterialTypes";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { QueryClient } from "@tanstack/react-query";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import useAppStore from "@hooks/useAppStore";
import { useSceneTreeVersion } from "@hooks/useSceneTreeVersion";
import useSelectionManager from "@hooks/useSelectionManager";
import { deserializeConcatenatedToInternedNodeDtos } from "@memorypack/deserializeInternedNodeDto";
import { deserializeConcatenatedNodeArrays } from "@memorypack/deserializeUtils";
import { isOPFSSupported } from "@/types/storage/opfsStorage";
import { createAndSetStorage, setInterningEnabled } from "@/types/internNodes";
import type { INodeDto } from "@/types/INodeDto";
import { loadSceneFromOPFS as loadSceneFromOPFSClient } from "@/workers/sceneBuilderClient";
import {
  createScenarioModelNodesKey,
  useAllScenariosProjectNodeData,
} from "./useAllScenariosProjectNodeData";
import { useAssignedCo2MaterialTypeIds } from "./useAssignedCo2MaterialTypeIds";

export interface UseProjectAnalysisDataParams {
  projectId: string | undefined;
  /** Grid API ref from Assign tab (optional; Assign tab has its own useModelsData). */
  assignGridApiRef?: MutableRefObject<GridApiPremium> | null | undefined;
  /** React Query client (reserved for cache invalidation from Assign tab). */
  queryClient: QueryClient;
}

export interface UseProjectAnalysisDataResult {
  /** Input for ProjectAnalysis; null when data not ready */
  data: ProjectAnalysisInput | null;
  /** CO₂ materials referenced by assigned elements (lite fields for chart/grid labels) */
  co2MaterialTypes: Co2MaterialType[] | undefined;
  isFetching: boolean;
  isSuccess: boolean;
  /** True when we have at least one scenario entry with models (can run analysis) */
  hasAnalysisData: boolean;
}

/**
 * Assembles ProjectAnalysisInput from scenarios, OPFS static nodes, and per-scenario materials/supplementals.
 * Loads node data for every scenario in the project so Results charts never use another scenario's materials by nodeId.
 */
export function useProjectAnalysisData({
  projectId,
}: UseProjectAnalysisDataParams): UseProjectAnalysisDataResult {
  const appStore = useAppStore();
  const sceneTreeVersion = useSceneTreeVersion();
  const selectionManager = useSelectionManager();
  const scenariosQuery = useScenarios(projectId ?? undefined);
  const scene = appStore.sceneManager.scene;

  const firstScenario = scenariosQuery.data?.[0];
  const selectedScenarioIds = (() => {
    const ids = selectionManager.scenarioIds;
    if (ids?.length > 0) return ids;
    const single = selectionManager.selectedScenarioId ?? firstScenario?.scenarioId;
    return single ? [single] : [];
  })();

  const allScenarioIds = useMemo(
    () => scenariosQuery.data?.map((s) => s.scenarioId).filter((id): id is string => !!id) ?? [],
    [scenariosQuery.data],
  );

  const modelIdsByScenario = useMemo(() => {
    const map = new Map<string, string[]>();
    scenariosQuery.data?.forEach((scenario) => {
      const scenarioId = scenario.scenarioId;
      if (!scenarioId) return;
      const ids =
        scenario.scenarioModels?.map((sm) => sm.modelId).filter((id): id is string => id != null) ?? [];
      map.set(scenarioId, ids);
    });
    return map;
  }, [scenariosQuery.data]);

  const allScenariosNodeData = useAllScenariosProjectNodeData({
    projectId,
    scenarioIds: allScenarioIds,
    modelIdsByScenario,
  });

  // Union of selected model IDs across selected scenarios (for scene load / viewer).
  const selectedModelIds = (() => {
    if (!scenariosQuery.data?.length) return [];
    const set = new Set<string>();
    for (const scenarioId of selectedScenarioIds) {
      const ids = selectionManager.getSelectedModelIds(scenarioId);
      if (ids.length > 0) {
        ids.forEach((id) => set.add(id));
      } else {
        const scenario = scenariosQuery.data.find((s) => s.scenarioId === scenarioId);
        scenario?.scenarioModels?.forEach((sm) => {
          if (sm.modelId) set.add(sm.modelId);
        });
      }
    }
    return Array.from(set);
  })();

  // Fetch only emission factors referenced by assigned elements (not the full catalog).
  const assignedCo2MaterialTypeIds = useAssignedCo2MaterialTypeIds(allScenariosNodeData.nodeMaterialLookup);
  const co2MaterialTypesQuery = useCo2MaterialTypesByIds(assignedCo2MaterialTypeIds);

  const cadModelCategoriesQuery = useCadModelCategories();

  const selectedScenariosRef = useRef<Array<typeof scenariosQuery.data extends (infer S)[] ? S : never>>([]);
  const selectedModelIdsRef = useRef<string[]>([]);
  const projectModelsRef = useRef(selectionManager.selectedProject?.models ?? []);

  selectedScenariosRef.current =
    scenariosQuery.data && selectedScenarioIds.length > 0
      ? selectedScenarioIds
          .map((id) => scenariosQuery.data!.find((s) => s.scenarioId === id))
          .filter((s): s is NonNullable<typeof s> => s != null)
      : [];
  selectedModelIdsRef.current = selectedModelIds;
  projectModelsRef.current = selectionManager.selectedProject?.models ?? [];

  const loadSceneFromOPFS = useCallback(async () => {
    const scenarios = selectedScenariosRef.current;
    const modelIds = selectedModelIdsRef.current;
    const projectModels = projectModelsRef.current ?? [];

    if (!modelIds.length || !scenarios.length || !isOPFSSupported()) return;

    const models = modelIds
      .map((modelId) => {
        for (const scenario of scenarios) {
          const scenarioModel = scenario.scenarioModels?.find((sm) => sm.modelId === modelId);
          const model = scenarioModel?.model ?? projectModels.find((m) => m.modelId === modelId);
          if (model) return model;
        }
        return null;
      })
      .filter((m): m is NonNullable<typeof m> => m != null);

    if (!models.length) return;

    const result = await loadSceneFromOPFSClient(Array.from(modelIds));
    const enableInterning = appStore.enableStringInterning;
    setInterningEnabled(enableInterning);
    if (enableInterning) {
      createAndSetStorage();
    }
    const nodesByModelId: Record<string, INodeDto[] | null> = {};
    for (let i = 0; i < modelIds.length; i++) {
      const modelId = modelIds[i]!;
      const buffer = result.buffersByModelId[modelId];
      if (!buffer) {
        nodesByModelId[modelId] = null;
        continue;
      }
      nodesByModelId[modelId] = enableInterning
        ? deserializeConcatenatedToInternedNodeDtos(buffer) ?? null
        : deserializeConcatenatedNodeArrays(buffer) ?? null;
    }
    appStore.sceneManager.populateSceneFromModelsAndNodes(models, nodesByModelId);
  }, [appStore.sceneManager, appStore.enableStringInterning]);

  useEffect(() => {
    const onDrained = () => {
      console.log("[useProjectAnalysisData] Downloads drained - loading scene from OPFS");
      loadSceneFromOPFS();
    };

    appStore.downloadManager.emitter.on(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, onDrained);

    if (appStore.downloadManager.isDrained()) {
      queueMicrotask(() => loadSceneFromOPFS());
    }

    return () => appStore.downloadManager.emitter.off(DownloadManagerEvents.DOWNLOADS_DRAINED_EVENT, onDrained);
  }, [appStore.downloadManager, loadSceneFromOPFS]);

  const input = useMemo((): ProjectAnalysisInput | null => {
    if (!projectId || !scenariosQuery.data?.length) return null;
    const projectModels = selectionManager.selectedProject?.models ?? [];
    const scenarioEntries: ProjectAnalysisScenarioEntry[] = [];
    const { scenarioNodesByScenarioModel, nodeMaterialLookup, nodeSupplementalLookup, materialsLoadedScenarioIds } =
      allScenariosNodeData;

    for (const scenario of scenariosQuery.data) {
      const scenarioId = scenario.scenarioId;
      if (!scenarioId) continue;
      const modelIdsForScenario =
        selectionManager.getSelectedModelIds(scenarioId).length > 0
          ? selectionManager.getSelectedModelIds(scenarioId)
          : scenario.scenarioModels?.map((sm) => sm.modelId).filter((id): id is string => id != null) ?? [];
      const modelEntries: ProjectAnalysisModelEntry[] = modelIdsForScenario
        .map((modelId): ProjectAnalysisModelEntry | null => {
          const scenarioModel = scenario.scenarioModels?.find((sm) => sm.modelId === modelId);
          if (!scenarioModel) return null;
          const model = scenarioModel.model ?? projectModels.find((m) => m.modelId === modelId);
          if (!model) return null;

          const instanceModel = scene.getModel(modelId) ?? undefined;
          const scenarioNodes = scenarioNodesByScenarioModel.get(createScenarioModelNodesKey(scenarioId, modelId));

          return {
            instanceModel,
            scenarioModel: scenarioModel.model ? scenarioModel : (Object.assign({}, scenarioModel, { model }) as typeof scenarioModel),
            scenarioNodes,
          };
        })
        .filter((e): e is ProjectAnalysisModelEntry => e !== null);
      if (modelEntries.length > 0) {
        scenarioEntries.push({ scenario, modelEntries });
      }
    }

    if (scenarioEntries.length === 0) return null;

    const cadModelCategories =
      (cadModelCategoriesQuery.data?.length ?? 0) > 0
        ? new Map(cadModelCategoriesQuery.data!.map((c) => [c.cadModelCategoryId!, c.label!]))
        : undefined;

    return {
      projectId,
      scenarioEntries,
      selectedScenarioIds,
      nodeMaterialLookup,
      nodeSupplementalLookup,
      materialsLoadedScenarioIds,
      cadModelCategories: cadModelCategories || undefined,
    };
  }, [
    projectId,
    selectedScenarioIds,
    scenariosQuery.data,
    selectionManager.selectedProject?.models,
    scene.getModelCount(),
    scene.getTotalNodeCount(),
    sceneTreeVersion,
    allScenariosNodeData.nodeMaterialLookup,
    allScenariosNodeData.nodeSupplementalLookup,
    allScenariosNodeData.scenarioNodesByScenarioModel,
    allScenariosNodeData.materialsLoadedScenarioIds,
    cadModelCategoriesQuery.data,
  ]);

  const isFetching =
    scenariosQuery.isFetching ||
    allScenariosNodeData.isFetching ||
    (assignedCo2MaterialTypeIds.length > 0 && co2MaterialTypesQuery.isFetching);
  const isSuccess =
    scenariosQuery.isSuccess &&
    (allScenarioIds.length === 0 || allScenariosNodeData.isSuccess);
  const hasAnalysisData =
    input !== null && input.scenarioEntries.length > 0 && scene.getTotalNodeCount() > 0;

  return {
    data: input,
    co2MaterialTypes: co2MaterialTypesQuery.data,
    isFetching,
    isSuccess,
    hasAnalysisData,
  };
}
